export const createExportListScheduler = ({ request, onUpdate, onError = () => {} }) => {
  let stopped = false
  let activeRequest = null
  let refreshQueued = false
  let pollTimer
  let timeoutTimer

  const fetchList = async () => {
    refreshQueued = false
    const controller = new AbortController()
    activeRequest = controller
    // This also bounds reading the response body, not just receiving its headers.
    timeoutTimer = setTimeout(() => controller.abort(), 30000)
    let pollAgain = true

    try {
      const response = await request(controller.signal)
      if (!response.ok) {
        // Retry transient errors, but not a missing permission or an invalid session.
        pollAgain = response.status === 408 || response.status === 429 || response.status >= 500
        if (!stopped && !refreshQueued) onError()
        return
      }
      const data = await response.json()
      // Creation may have queued a fresh read while this request was in flight.
      if (stopped || refreshQueued || controller.signal.aborted) return

      const items = Array.isArray(data) ? data : []
      onUpdate(items)
      pollAgain = items.some((item) => String(item.status || '').toLowerCase() === 'pending')
    } catch {
      // Keep the displayed list and retry, even if it has no pending exports yet.
      if (!stopped && !refreshQueued) onError()
    } finally {
      clearTimeout(timeoutTimer)
      activeRequest = null
      if (!stopped) {
        if (refreshQueued) {
          // A creation-triggered refresh does not wait for the five-second poll delay.
          void fetchList()
        } else if (pollAgain) {
          pollTimer = setTimeout(fetchList, 5000)
        }
      }
    }
  }

  return {
    refresh() {
      if (stopped) return
      clearTimeout(pollTimer)
      if (activeRequest) {
        // Coalesce repeated refreshes and ignore the pre-creation response.
        refreshQueued = true
      } else {
        void fetchList()
      }
    },
    stop() {
      stopped = true
      clearTimeout(pollTimer)
      clearTimeout(timeoutTimer)
      activeRequest?.abort()
    }
  }
}
